import { Component, ref, markRaw } from 'vue';

import { defineStore } from 'pinia';

import PopupToast from '@/components/domains/mobile/common/PopupToast.vue';
import { PopupOptions, PopupItem } from '@/types/components';
import getSocketId from '@/utils/getSocketId';

export const usePopupStore = defineStore('popup', () => {
  const popups = ref<PopupItem[]>([]);

  const open = (component: Component, options: PopupOptions) => {
    const id = options.id || getSocketId();

    popups.value.push({
      component: markRaw(component),
      id,
      title: '',
      subTitle: '',
      isOpen: false,
      showHandleBar: false,
      showCloseButton: false,
      cancelButton: { text: '' },
      confirmButton: { text: '' },
      backButton: { text: '' },
      ...options,
    });

    setTimeout(() => {
      const popup = popups.value.find((p) => p.id === id);
      if (popup) {
        popup.isOpen = true;
      }
    }, 10);

    return id;
  };

  const openToast = (message: string) => {
    const isToastOpen = popups.value.some((popup) => popup.component === PopupToast);
    if (isToastOpen) {
      return;
    }

    const id = getSocketId();

    popups.value.push({
      component: markRaw(PopupToast),
      id,
      title: message,
      subTitle: '',
      isOpen: false,
      showHandleBar: false,
      showCloseButton: false,
      cancelButton: { text: '' },
      confirmButton: { text: '' },
      backButton: { text: '' },
    });

    setTimeout(() => {
      const popup = popups.value.find((p) => p.id === id);
      if (popup) {
        popup.isOpen = true;
      }
    }, 10);

    return id;
  };

  const close = (id: string) => {
    const index = popups.value.findIndex((p) => p.id === id);
    if (index !== -1) {
      const popup = popups.value[index];
      popup.isOpen = false;

      setTimeout(() => {
        if (popup.onClose) {
          popup.onClose();
        }
        popups.value.splice(index, 1);
      }, 100);
    }
  };

  const closeLast = () => {
    const popupLast = popups.value.at(-1);
    if (!popupLast) return;
    close(popupLast.id ?? '');
  };

  const closeAll = () => {
    popups.value.forEach((popup) => {
      popup.isOpen = false;
    });

    setTimeout(() => {
      popups.value.forEach((popup) => {
        if (popup.onClose) {
          popup.onClose();
        }
      });
      popups.value = [];
    }, 300);
  };

  const cancel = async (id: string) => {
    const popup = popups.value.find((p) => p.id === id);
    if (popup) {
      if (popup.cancelButton?.action) {
        await popup.cancelButton?.action();
      }
      close(id);
    }
  };

  const confirm = async (id: string) => {
    const popup = popups.value.find((p) => p.id === id);
    if (popup) {
      if (popup.confirmButton?.action) {
        await popup.confirmButton?.action();
      }
      close(id);
    }
  };

  const back = async (id: string) => {
    const popup = popups.value.find((p) => p.id === id);
    if (popup) {
      if (popup.backButton?.action) {
        await popup.backButton?.action();
      }
      close(id);
    }
  };

  return {
    popups,
    open,
    openToast,
    close,
    closeLast,
    closeAll,
    cancel,
    confirm,
    back,
  };
});
